import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Snackbar, Alert, CircularProgress, InputAdornment, useMediaQuery, Divider, Chip,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMutation } from "react-query";
import client from "../../../../../API";
import { getPONumber } from "../../../../../Utils/constants";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import NotesIcon from '@mui/icons-material/Notes';

const PayoutDialog = ({ 
  open, 
  onClose, 
  partnershipId, 
  creatorId, 
  conversationId,
  onPayoutCreated 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [poNumber, setPoNumber] = useState("");
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Form validation
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!poNumber && open) {
      setPoNumber(getPONumber('PO'));
    }
  }, [poNumber, open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setAmount("");
      setNotes("");
      setErrors({});
      setShowError(false);
      setShowSuccess(false);
    }
  }, [open]);

  const createDeliverableMutation = useMutation(client.deliverables.create, {
    onMutate: () => {
      setIsLoading(true);
      setErrors({});
    },
    onSuccess: () => {
      setIsLoading(false);
      setShowSuccess(true);
      
      // Auto-close after success message
      setTimeout(() => {
        onClose();
        if (onPayoutCreated) {
          onPayoutCreated();
        }
      }, 1500);
    },
    onError: (error) => {
      setIsLoading(false);
      console.error("Error creating payout:", error);
      setShowError(true);
      setErrorMessage(
        `Error creating payout: ${error?.response?.data?.error || error.message}`
      );
    },
  });

  // Handle amount input - only allow numbers and decimal points
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty string, integers, or decimals with up to 2 places
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setAmount(value);
      // Clear amount error when user starts typing
      if (errors.amount) {
        setErrors(prev => ({ ...prev, amount: null }));
      }
    }
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
    // Clear notes error when user starts typing
    if (errors.notes) {
      setErrors(prev => ({ ...prev, notes: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount greater than $0";
    }

    if (!notes.trim()) {
      newErrors.notes = "Please provide notes for this payout";
    }

    if (!poNumber.trim()) {
      newErrors.poNumber = "PO Number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    createDeliverableMutation.mutate({
      partnership_id: partnershipId,
      creator_id: creatorId,
      conversation_id: conversationId,
      deliverable_type: "Payout",
      deliverable_link: null,
      amount: parseFloat(amount),
      status: "sent",
      notes: notes.trim(),
      due_date: null,
      platforms: [],
      po_number: poNumber.trim().toUpperCase(),
    });
  };

  const isFormValid = amount && parseFloat(amount) > 0 && notes.trim() && poNumber.trim();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[10],
            overflow: "hidden",
            maxWidth: { xs: "95vw", sm: "600px" },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: { xs: 2, sm: 2.5 },
            px: { xs: 2, sm: 3 },
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <AccountBalanceWalletIcon 
            color="primary" 
            sx={{ fontSize: { xs: 20, sm: 24 } }} 
          />
          <Typography 
            variant="h6" 
            component="div"
            sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
          >
            Create Payout
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: "background.default",
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            minHeight: { xs: 300, sm: 400 },
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          {/* Payout Summary Section */}
          <Box 
            sx={{
              p: 2,
              bgcolor: "background.paper",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <ReceiptIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" color="text.primary">
                Payout Details
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Create a direct payout for completed work or bonuses. This will be processed immediately upon creation.
            </Typography>
          </Box>

          {/* Amount Field */}
          <TextField
            fullWidth
            variant="outlined"
            label="Payout Amount"
            value={amount}
            onChange={handleAmountChange}
            error={!!errors.amount}
            helperText={errors.amount || "Enter the payout amount in USD"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.secondary">
                    $
                  </Typography>
                </InputAdornment>
              ),
              inputProps: {
                inputMode: "decimal",
                pattern: "[0-9]*\.?[0-9]{0,2}",
                placeholder: "0.00"
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
                },
              },
            }}
          />

          {/* Notes Field */}
          <TextField
            fullWidth
            variant="outlined"
            label="Payout Description"
            value={notes}
            onChange={handleNotesChange}
            error={!!errors.notes}
            helperText={errors.notes || "Describe the reason for this payout"}
            multiline
            rows={4}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                  <NotesIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
            sx={{
              "& .MuiInputBase-root": {
                alignItems: "flex-start",
              },
              "& .MuiOutlinedInput-root": {
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
                },
              },
            }}
          />

          {/* PO Number Field */}
          <TextField
            fullWidth
            variant="outlined"
            label="Purchase Order Number"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value.toUpperCase())}
            error={!!errors.poNumber}
            helperText={errors.poNumber || "Auto-generated PO number for tracking"}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              "& .MuiInputBase-input": {
                textTransform: "uppercase",
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
              "& .MuiOutlinedInput-root": {
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  boxShadow: `0 0 0 1px ${theme.palette.primary.main}25`,
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`,
                },
              },
            }}
          />

          {/* Fee Information */}
          <Box 
            sx={{
              p: 2,
              bgcolor: "info.light",
              borderRadius: 2,
              border: `1px solid ${theme.palette.info.main}20`,
            }}
          >
            <Typography variant="body2" color="info.dark" sx={{ mb: 0.5 }}>
              <strong>Processing Information</strong>
            </Typography>
            <Typography variant="caption" color="info.dark">
              • A 5% processing fee applies to all payouts
              {amount && parseFloat(amount) > 0 && (
                <>
                  <br />• Net payout amount: ${(parseFloat(amount) * 0.95).toFixed(2)}
                </>
              )}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: "background.paper",
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1.5,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "stretch",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isLoading}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
              order: { xs: 2, sm: 1 },
              "&:hover": {
                bgcolor: "action.hover",
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[2],
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading || !isFormValid}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              order: { xs: 1, sm: 2 },
              minWidth: { xs: "auto", sm: 140 },
              bgcolor: "primary.main",
              boxShadow: theme.shadows[3],
              "&:hover": {
                bgcolor: "primary.dark",
                transform: "translateY(-1px)",
                boxShadow: theme.shadows[6],
              },
              "&:disabled": {
                bgcolor: "action.disabledBackground",
                color: "action.disabled",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} color="inherit" />
                Processing...
              </Box>
            ) : (
              "Create Payout"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            "& .MuiAlert-icon": {
              fontSize: "1.2rem",
            },
          }}
        >
          Payout created successfully! Processing payment...
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
            "& .MuiAlert-icon": {
              fontSize: "1.2rem",
            },
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PayoutDialog;